// src/components/dashboard/mastercard/TermsAndConditionsModal.tsx
import React from 'react';
import { X } from 'lucide-react';

interface TermsAndConditionsModalProps {
  isOpen: boolean;
  onAccept: () => void;
  onDecline: () => void;
  isLoading?: boolean;
}

export function TermsAndConditionsModal({ 
  isOpen, 
  onAccept, 
  onDecline,
  isLoading = false 
}: TermsAndConditionsModalProps) {
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop con blur */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={isLoading ? undefined : onDecline}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            Quicko MasterCard Terms and Conditions
          </h2>
          {!isLoading && (
            <button
              onClick={onDecline}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="prose prose-gray max-w-none">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Introduction</h3>
            <p className="text-gray-600 mb-4">
              These Terms and Conditions govern the use of your Belobaba MasterCard Virtual Card issued by Quicko.
              By applying for and using the card, you agree to be bound by these Terms and Conditions.
            </p>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">1. Definitions</h3>
            <p className="text-gray-600 mb-4">
              <strong>"Card"</strong> means the Belobaba MasterCard Virtual Card issued to you.<br />
              <strong>"Issuer"</strong> means Quicko, the financial institution that issues the Card.<br />
              <strong>"We", "us", "our"</strong> refers to Belobaba and/or the Issuer as appropriate.<br />
              <strong>"You", "your"</strong> refers to the person who applies for and is issued a Card.
            </p>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">2. Card Issuance and Activation</h3>
            <p className="text-gray-600 mb-4">
              2.1. Eligibility: You must be at least 18 years old and have completed our identity verification process.<br />
              2.2. Your Card will be activated upon successful completion of the application process.<br />
              2.3. The Card is virtual only and no physical card will be issued.
            </p>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">3. Card Usage</h3>
            <p className="text-gray-600 mb-4">
              3.1. The Card can be used for online and contactless transactions where MasterCard is accepted.<br />
              3.2. The Card cannot be used for certain restricted activities as detailed in our Acceptable Use Policy.<br />
              3.3. You must not use the Card for any illegal purposes.
            </p>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">4. Fees and Charges</h3>
            <p className="text-gray-600 mb-4">
              4.1. We may charge fees for the use of the Card as set out in our Fee Schedule.<br />
              4.2. All applicable fees will be deducted from your Card balance.<br />
              4.3. Foreign currency transactions may incur additional fees.
            </p>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">5. Card Security</h3>
            <p className="text-gray-600 mb-4">
              5.1. You must keep your Card details secure at all times.<br />
              5.2. You must not share your Card details, PIN, or security information with anyone else.<br />
              5.3. If you suspect that your Card security has been compromised, you must notify us immediately.
            </p>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">6. Lost or Stolen Cards</h3>
            <p className="text-gray-600 mb-4">
              6.1. You must notify us immediately if your device containing the virtual card is lost or stolen.<br />
              6.2. You may be liable for unauthorized transactions if you fail to notify us promptly.<br />
              6.3. We may suspend or cancel your Card if we suspect fraud or security concerns.
            </p>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">7. Liability</h3>
            <p className="text-gray-600 mb-4">
              7.1. We are not liable for any merchant's refusal to accept the Card.<br />
              7.2. We are not responsible for the quality of goods or services purchased with the Card.<br />
              7.3. Our liability is limited to the amount loaded on your Card.
            </p>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">8. Data Protection</h3>
            <p className="text-gray-600 mb-4">
              8.1. We will process your personal data in accordance with our Privacy Policy.<br />
              8.2. By using the Card, you consent to such processing.<br />
              8.3. We may share your data with third parties as necessary to provide Card services.
            </p>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">9. Changes to Terms</h3>
            <p className="text-gray-600 mb-4">
              9.1. We may change these Terms and Conditions at any time by giving you notice.<br />
              9.2. If you do not agree with the changes, you may close your Card account.<br />
              9.3. Your continued use of the Card after the changes take effect constitutes your acceptance of the changes.
            </p>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">10. Governing Law</h3>
            <p className="text-gray-600 mb-4">
              These Terms and Conditions are governed by the laws of Spain and you agree to the exclusive jurisdiction of the Spanish courts.
            </p>

            <div className="mt-8 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500 italic">
                Last updated: January 2025<br />
                Quicko Financial Services, S.L.<br />
                Licensed and regulated by the Bank of Spain
              </p>
            </div>
          </div>
        </div>

        {/* Footer with buttons */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onDecline}
            disabled={isLoading}
            className="px-6 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Decline
          </button>
          <button
            onClick={onAccept}
            disabled={isLoading}
            className="px-6 py-3 bg-[#003DA5] text-white rounded-lg font-medium hover:bg-[#B3A369] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Processing...
              </>
            ) : (
              'I Accept Terms and Conditions'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}